// src/utils/cvUtils.js — Shared Canvas API Computer Vision utilities

export function generateSynth(type, size = 256) {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  switch (type) {
    case 'gradient': {
      const g = ctx.createLinearGradient(0, 0, size, size);
      g.addColorStop(0,'#00d9ff');g.addColorStop(.5,'#b08aff');g.addColorStop(1,'#ff3556');
      ctx.fillStyle=g;ctx.fillRect(0,0,size,size);
      break;
    }
    case 'shapes': {
      ctx.fillStyle='#050e16';ctx.fillRect(0,0,size,size);
      ctx.fillStyle='#00d9ff';ctx.fillRect(20,20,80,80);
      ctx.fillStyle='#ff3556';ctx.beginPath();ctx.arc(190,70,50,0,Math.PI*2);ctx.fill();
      ctx.fillStyle='#29ffa5';ctx.beginPath();ctx.moveTo(128,140);ctx.lineTo(210,235);ctx.lineTo(46,235);ctx.closePath();ctx.fill();
      ctx.fillStyle='#ffbe30';ctx.fillRect(30,160,70,70);
      ctx.fillStyle='#b08aff';ctx.beginPath();ctx.arc(200,195,35,0,Math.PI*2);ctx.fill();
      break;
    }
    case 'circles': {
      ctx.fillStyle='#0a1520';ctx.fillRect(0,0,size,size);
      const colors=['#00d9ff','#ff3556','#29ffa5','#ffbe30','#b08aff','#ff7040','#f472b6','#34d399'];
      for(let i=0;i<8;i++){const r=20+Math.floor(i*12);const x=30+i*28;const y=60+((i%2)*130);ctx.fillStyle=colors[i];ctx.beginPath();ctx.arc(x,y,r,0,Math.PI*2);ctx.fill();}
      break;
    }
    case 'checker': {
      const sq=size/8;
      for(let r=0;r<8;r++)for(let col=0;col<8;col++){ctx.fillStyle=(r+col)%2===0?'#ffffff':'#000000';ctx.fillRect(col*sq,r*sq,sq,sq);}
      break;
    }
    case 'noise': {
      const id=ctx.createImageData(size,size);
      for(let i=0;i<id.data.length;i+=4){const v=Math.random()*255;id.data[i]=id.data[i+1]=id.data[i+2]=v;id.data[i+3]=255;}
      ctx.putImageData(id,0,0);
      break;
    }
    case 'portrait': {
      ctx.fillStyle='#d4a57a';ctx.fillRect(0,0,size,size);
      const rg=ctx.createRadialGradient(size*.5,size*.38,size*.05,size*.5,size*.38,size*.3);
      rg.addColorStop(0,'#f5cba7');rg.addColorStop(1,'#c68642');
      ctx.fillStyle=rg;ctx.beginPath();ctx.ellipse(size*.5,size*.38,size*.22,size*.28,0,0,Math.PI*2);ctx.fill();
      ctx.fillStyle='#3d2005';ctx.beginPath();ctx.ellipse(size*.5,size*.16,size*.28,size*.15,0,0,Math.PI*2);ctx.fill();
      break;
    }
    default: {
      const g=ctx.createLinearGradient(0,0,size,0);
      g.addColorStop(0,'#f00');g.addColorStop(.17,'#ff0');g.addColorStop(.33,'#0f0');g.addColorStop(.5,'#0ff');g.addColorStop(.67,'#00f');g.addColorStop(.83,'#f0f');g.addColorStop(1,'#f00');
      ctx.fillStyle=g;ctx.fillRect(0,0,size,size);
    }
  }
  return c.toDataURL();
}

export function imgToImageData(src) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const s=Math.min(img.width,img.height,384);
      const c=document.createElement('canvas');c.width=c.height=s;
      c.getContext('2d').drawImage(img,0,0,s,s);
      resolve(c.getContext('2d').getImageData(0,0,s,s));
    };
    img.src = src;
  });
}

export function imageDataToURL(id) {
  const c=document.createElement('canvas');c.width=id.width;c.height=id.height;
  c.getContext('2d').putImageData(id,0,0);return c.toDataURL();
}

export function cloneID(id) {
  return new ImageData(new Uint8ClampedArray(id.data),id.width,id.height);
}

export function toGray(id) {
  const d=cloneID(id);
  for(let i=0;i<d.data.length;i+=4){const v=0.299*d.data[i]+0.587*d.data[i+1]+0.114*d.data[i+2];d.data[i]=d.data[i+1]=d.data[i+2]=v;}
  return d;
}

export function convolve(id, kernel, divisor=1, offset=0) {
  const {width:w,height:h,data:src}=id;const dst=new Uint8ClampedArray(src.length);
  const ks=Math.floor(kernel.length/2);
  for(let y=0;y<h;y++)for(let x=0;x<w;x++){
    let r=0,g=0,b=0;
    for(let ky=-ks;ky<=ks;ky++)for(let kx=-ks;kx<=ks;kx++){
      const py=Math.min(h-1,Math.max(0,y+ky)),px=Math.min(w-1,Math.max(0,x+kx));
      const idx=(py*w+px)*4,wt=kernel[ky+ks][kx+ks];
      r+=src[idx]*wt;g+=src[idx+1]*wt;b+=src[idx+2]*wt;
    }
    const i=(y*w+x)*4;
    dst[i]=Math.min(255,Math.max(0,r/divisor+offset));
    dst[i+1]=Math.min(255,Math.max(0,g/divisor+offset));
    dst[i+2]=Math.min(255,Math.max(0,b/divisor+offset));
    dst[i+3]=255;
  }
  return new ImageData(dst,w,h);
}

export const KERNELS = {
  identity:  [[0,0,0],[0,1,0],[0,0,0]],
  blur:      [[1,1,1],[1,1,1],[1,1,1]],
  gaussian3: [[1,2,1],[2,4,2],[1,2,1]],
  gaussian5: [[1,4,6,4,1],[4,16,24,16,4],[6,24,36,24,6],[4,16,24,16,4],[1,4,6,4,1]],
  sharpen:   [[0,-1,0],[-1,5,-1],[0,-1,0]],
  unsharp:   [[-1,-1,-1],[-1,9,-1],[-1,-1,-1]],
  emboss:    [[-2,-1,0],[-1,1,1],[0,1,2]],
  sobelX:    [[-1,0,1],[-2,0,2],[-1,0,1]],
  sobelY:    [[-1,-2,-1],[0,0,0],[1,2,1]],
  prewittX:  [[-1,0,1],[-1,0,1],[-1,0,1]],
  prewittY:  [[-1,-1,-1],[0,0,0],[1,1,1]],
  laplacian: [[0,1,0],[1,-4,1],[0,1,0]],
  ridges:    [[-1,-1,-1],[-1,8,-1],[-1,-1,-1]],
};

export function sobel(id) {
  const gray=toGray(id);const{width:w,height:h,data:src}=gray;
  const dst=new Uint8ClampedArray(src.length);
  for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){
    const px=(r,c)=>src[((y+r)*w+(x+c))*4];
    const gx=-px(-1,-1)+px(-1,1)-2*px(0,-1)+2*px(0,1)-px(1,-1)+px(1,1);
    const gy=-px(-1,-1)-2*px(-1,0)-px(-1,1)+px(1,-1)+2*px(1,0)+px(1,1);
    const m=Math.min(255,Math.sqrt(gx*gx+gy*gy));
    const i=(y*w+x)*4;dst[i]=dst[i+1]=dst[i+2]=m;dst[i+3]=255;
  }
  return new ImageData(dst,w,h);
}

export function prewittEdge(id, dir='x') {
  const gray=toGray(id);const{width:w,height:h,data:src}=gray;
  const dst=new Uint8ClampedArray(src.length);
  for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){
    const px=(r,c)=>src[((y+r)*w+(x+c))*4];
    const g=dir==='x'?(-px(-1,-1)+px(-1,1)-px(0,-1)+px(0,1)-px(1,-1)+px(1,1)):(-px(-1,-1)-px(-1,0)-px(-1,1)+px(1,-1)+px(1,0)+px(1,1));
    const m=Math.min(255,Math.abs(g));const i=(y*w+x)*4;dst[i]=dst[i+1]=dst[i+2]=m;dst[i+3]=255;
  }
  return new ImageData(dst,w,h);
}

export function laplacianEdge(id) {
  const gray=toGray(id);
  return convolve(gray,[[0,1,0],[1,-4,1],[0,1,0]],1,128);
}

export function logEdge(id) {
  const blurred=convolve(toGray(id),KERNELS.gaussian5,256,0);
  return convolve(blurred,[[0,1,0],[1,-4,1],[0,1,0]],1,128);
}

export function ridgeEdge(id) {
  return convolve(toGray(id),KERNELS.ridges,1,0);
}

export function cannyEdge(id, lo=50, hi=150) {
  const gray=toGray(id);const{width:w,height:h}=gray;
  const blurred=convolve(gray,KERNELS.gaussian3,16,0);const src=blurred.data;
  const mag=new Float32Array(w*h),ang=new Float32Array(w*h);
  for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){
    const px=(r,c)=>src[((y+r)*w+(x+c))*4];
    const gx=-px(-1,-1)+px(-1,1)-2*px(0,-1)+2*px(0,1)-px(1,-1)+px(1,1);
    const gy=-px(-1,-1)-2*px(-1,0)-px(-1,1)+px(1,-1)+2*px(1,0)+px(1,1);
    mag[y*w+x]=Math.sqrt(gx*gx+gy*gy);
    ang[y*w+x]=Math.atan2(gy,gx)*180/Math.PI;
  }
  const nms=new Float32Array(w*h);
  for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){
    const a=((ang[y*w+x]%180)+180)%180;
    let n1,n2;
    if(a<22.5||a>=157.5){n1=mag[y*w+x-1];n2=mag[y*w+x+1];}
    else if(a<67.5){n1=mag[(y-1)*w+x+1];n2=mag[(y+1)*w+x-1];}
    else if(a<112.5){n1=mag[(y-1)*w+x];n2=mag[(y+1)*w+x];}
    else{n1=mag[(y-1)*w+x-1];n2=mag[(y+1)*w+x+1];}
    nms[y*w+x]=mag[y*w+x]>=n1&&mag[y*w+x]>=n2?mag[y*w+x]:0;
  }
  const dst=new Uint8ClampedArray(w*h*4);
  const STRONG=255,WEAK=80;
  for(let i=0;i<w*h;i++){const v=nms[i]>=hi?STRONG:nms[i]>=lo?WEAK:0;dst[i*4]=dst[i*4+1]=dst[i*4+2]=v;dst[i*4+3]=255;}
  for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){
    if(dst[(y*w+x)*4]===WEAK){
      let hs=false;
      for(let dy=-1;dy<=1;dy++)for(let dx=-1;dx<=1;dx++)if(dst[((y+dy)*w+(x+dx))*4]===STRONG)hs=true;
      dst[(y*w+x)*4]=dst[(y*w+x)*4+1]=dst[(y*w+x)*4+2]=hs?STRONG:0;
    }
  }
  return new ImageData(dst,w,h);
}

export function morphOp(id, radius, op) {
  const gray=toGray(id);const{width:w,height:h,data:src}=gray;
  const dst=new Uint8ClampedArray(src.length);const r2=radius*radius;
  const offs=[];for(let dy=-radius;dy<=radius;dy++)for(let dx=-radius;dx<=radius;dx++)if(dy*dy+dx*dx<=r2)offs.push([dy,dx]);
  for(let y=0;y<h;y++)for(let x=0;x<w;x++){
    let best=op==='dilate'?0:255,vals=[];
    for(const[dy,dx]of offs){const py=Math.min(h-1,Math.max(0,y+dy)),px=Math.min(w-1,Math.max(0,x+dx)),v=src[(py*w+px)*4];
      if(op==='dilate')best=Math.max(best,v);else if(op==='erode')best=Math.min(best,v);else vals.push(v);}
    if(op==='median'){vals.sort((a,b)=>a-b);best=vals[Math.floor(vals.length/2)];}
    const i=(y*w+x)*4;dst[i]=dst[i+1]=dst[i+2]=best;dst[i+3]=255;
  }
  return new ImageData(dst,w,h);
}

export function rgbToHsv(r,g,b){r/=255;g/=255;b/=255;const max=Math.max(r,g,b),min=Math.min(r,g,b),d=max-min;let h=0;if(d>0){if(max===r)h=((g-b)/d)%6;else if(max===g)h=(b-r)/d+2;else h=(r-g)/d+4;h=Math.round(h*60+360)%360;}return{h,s:max===0?0:Math.round(d/max*100),v:Math.round(max*100)};}

export function colorSpaceChannels(id, mode) {
  const{width:w,height:h,data:src}=id;
  const ch=[new Uint8ClampedArray(w*h*4),new Uint8ClampedArray(w*h*4),new Uint8ClampedArray(w*h*4)];
  for(let i=0;i<w*h;i++){
    const r=src[i*4],g=src[i*4+1],b=src[i*4+2];let c0,c1,c2;
    if(mode==='hsv'){const h2=rgbToHsv(r,g,b);c0=Math.round(h2.h/360*255);c1=Math.round(h2.s*2.55);c2=Math.round(h2.v*2.55);}
    else if(mode==='ycbcr'){c0=Math.round(.299*r+.587*g+.114*b);c1=Math.round(128-.168736*r-.331264*g+.5*b);c2=Math.round(128+.5*r-.418688*g-.081312*b);}
    else{const L=.2126*r/255+.7152*g/255+.0722*b/255;c0=Math.round(L*255);c1=Math.round((r/255-g/255)*127+128);c2=Math.round((g/255-b/255)*127+128);}
    for(let ci=0;ci<3;ci++){const j=i*4,v=[c0,c1,c2][ci];ch[ci][j]=ch[ci][j+1]=ch[ci][j+2]=v;ch[ci][j+3]=255;}
  }
  return ch.map(d=>new ImageData(d,w,h));
}

export function harrisCorners(id, k=0.04, thresh=0.01) {
  const gray=toGray(id);const{width:w,height:h,data:src}=gray;
  const Ix=new Float32Array(w*h),Iy=new Float32Array(w*h);
  for(let y=1;y<h-1;y++)for(let x=1;x<w-1;x++){Ix[y*w+x]=(src[(y*w+x+1)*4]-src[(y*w+x-1)*4])/2;Iy[y*w+x]=(src[((y+1)*w+x)*4]-src[((y-1)*w+x)*4])/2;}
  const R=new Float32Array(w*h);const ws=3;
  for(let y=ws;y<h-ws;y++)for(let x=ws;x<w-ws;x++){
    let a=0,b=0,c=0;
    for(let dy=-ws;dy<=ws;dy++)for(let dx=-ws;dx<=ws;dx++){const ix=Ix[(y+dy)*w+(x+dx)],iy=Iy[(y+dy)*w+(x+dx)];a+=ix*ix;b+=iy*iy;c+=ix*iy;}
    R[y*w+x]=a*b-c*c-k*(a+b)*(a+b);
  }
  const maxR=Math.max(...R)||1;
  const corners=[];
  for(let y=ws;y<h-ws;y++)for(let x=ws;x<w-ws;x++){
    if(R[y*w+x]>thresh*maxR){
      let isMax=true;for(let dy=-3;dy<=3&&isMax;dy++)for(let dx=-3;dx<=3&&isMax;dx++)if(R[(y+dy)*w+(x+dx)]>R[y*w+x])isMax=false;
      if(isMax)corners.push({x,y,score:+(R[y*w+x]/maxR).toFixed(4)});
    }
  }
  return corners.sort((a,b)=>b.score-a.score).slice(0,60);
}

export function otsuThreshold(id) {
  const gray=toGray(id);const{data}=gray;const hist=new Array(256).fill(0);
  for(let i=0;i<data.length;i+=4)hist[data[i]]++;
  const total=data.length/4;let sum=0;for(let i=0;i<256;i++)sum+=i*hist[i];
  let sumB=0,wB=0,wF=0,maxVar=0,t=0;
  for(let i=0;i<256;i++){wB+=hist[i];if(!wB)continue;wF=total-wB;if(!wF)break;sumB+=i*hist[i];const mB=sumB/wB,mF=(sum-sumB)/wF,bv=wB*wF*(mB-mF)**2;if(bv>maxVar){maxVar=bv;t=i;}}
  return t;
}

export function histEqualize(id) {
  const gray=toGray(id);const{width:w,height:h,data:src}=gray;
  const hist=new Array(256).fill(0);for(let i=0;i<src.length;i+=4)hist[src[i]]++;
  const total=w*h;const cdf=[];let cum=0;for(let i=0;i<256;i++){cum+=hist[i];cdf.push(cum);}
  const cdfMin=cdf.find(v=>v>0)||1;const lut=cdf.map(c=>Math.round((c-cdfMin)/(total-cdfMin)*255));
  const dst=new Uint8ClampedArray(src.length);
  for(let i=0;i<src.length;i+=4){const v=lut[src[i]];dst[i]=dst[i+1]=dst[i+2]=v;dst[i+3]=255;}
  return new ImageData(dst,w,h);
}

export function kmeansSegment(id, k=4, iters=10) {
  const{width:w,height:h,data:src}=id;
  const pixels=[];for(let i=0;i<src.length;i+=4)pixels.push([src[i],src[i+1],src[i+2]]);
  const step=Math.floor(pixels.length/k);
  let centroids=Array.from({length:k},(_,i)=>[...pixels[i*step]]);
  let labels=new Int32Array(pixels.length);
  for(let iter=0;iter<iters;iter++){
    for(let i=0;i<pixels.length;i++){let best=Infinity,bi=0;for(let ci=0;ci<k;ci++){const d=pixels[i].reduce((s,v,j)=>s+(v-centroids[ci][j])**2,0);if(d<best){best=d;bi=ci;}}labels[i]=bi;}
    const sums=Array.from({length:k},()=>[0,0,0]),counts=new Int32Array(k);
    for(let i=0;i<pixels.length;i++){sums[labels[i]][0]+=pixels[i][0];sums[labels[i]][1]+=pixels[i][1];sums[labels[i]][2]+=pixels[i][2];counts[labels[i]]++;}
    for(let ci=0;ci<k;ci++)if(counts[ci])centroids[ci]=sums[ci].map(s=>s/counts[ci]);
  }
  const dst=new Uint8ClampedArray(src.length);
  for(let i=0;i<pixels.length;i++){const c=centroids[labels[i]];dst[i*4]=c[0];dst[i*4+1]=c[1];dst[i*4+2]=c[2];dst[i*4+3]=255;}
  const clusterInfo=Array.from({length:k},(_,ci)=>({color:`rgb(${centroids[ci].map(v=>Math.round(v)).join(',')})`,count:Array.from(labels).filter(l=>l===ci).length,pct:+(Array.from(labels).filter(l=>l===ci).length/pixels.length*100).toFixed(1)}));
  return{imageData:new ImageData(dst,w,h),clusters:clusterInfo};
}

export function getHistogram(id, bins=32) {
  const{data}=id;const R=new Array(bins).fill(0),G=new Array(bins).fill(0),B=new Array(bins).fill(0),Y=new Array(bins).fill(0);
  const bw=256/bins;
  for(let i=0;i<data.length;i+=4){const bi=Math.min(bins-1,Math.floor(data[i]/bw));R[bi]++;G[Math.min(bins-1,Math.floor(data[i+1]/bw))]++;B[Math.min(bins-1,Math.floor(data[i+2]/bw))]++;Y[Math.min(bins-1,Math.floor((0.299*data[i]+0.587*data[i+1]+0.114*data[i+2])/bw))]++;}
  return Array.from({length:bins},(_,i)=>({bin:Math.round(i*bw),R:R[i],G:G[i],B:B[i],Y:Y[i]}));
}

